import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import style from './FieldLabel.css'

let fieldLabelId = 0

/**
 * Field Label is the label for a control inside a [Field](/components/field/).
 * It is styled to match the labels used by Nord form controls such as
 * [Input](/components/input/).
 *
 * The enclosing Field shares this label's id and text with the control through
 * context, so the control names itself (`aria-labelledby` for light-DOM controls,
 * `aria-label` for shadow-DOM ones) — no wiring needed. Set the `for` attribute to
 * a control's `id` to additionally get native-style click-to-focus.
 *
 * The required indicator is driven by the control: mark the control `required`
 * and the Field toggles this label's `required` attribute automatically.
 *
 * @status draft
 * @category form
 * @lightDom true
 *
 * @slot - Default slot for the label text.
 * @attr {boolean} required - Present while the control is required (set by the Field; not a settable prop).
 */
@customElement('nord-field-label')
export default class FieldLabel extends LitElement {
  /**
   * The `id` of the control this label describes. Enables native-style
   * click-to-focus, mirroring the native `<label for>` attribute.
   */
  @property({ reflect: true }) for?: string

  private handleClick = (event: MouseEvent) => {
    const control = this.controlForLabel()
    if (!control) {
      return
    }

    // If the click originated from the control itself (e.g. the label wraps a
    // checkbox in a choice card), let the control handle it natively — otherwise
    // we'd re-dispatch click() and toggle it twice. This mirrors native `<label>`.
    const target = event.target
    if (target instanceof Node && (control === target || control.contains(target))) {
      return
    }

    // Popup-trigger controls (e.g. Date Range Picker) should focus on label click
    // — showing the focus ring — not open. Keyboard Space/Enter then activates.
    if ((control as { labelFocusOnly?: boolean }).labelFocusOnly) {
      ;(control as HTMLElement & { focus: (o?: FocusOptions) => void }).focus?.({ focusVisible: true } as FocusOptions)
      return
    }

    // Replicate native label behaviour: activate the associated control. Form
    // controls (checkbox, toggle, radio) toggle on click; text controls focus.
    if (typeof (control as HTMLElement & { click?: () => void }).click === 'function') {
      ;(control as HTMLElement & { click: () => void }).click()
    }
    if (typeof (control as HTMLElement & { focus?: () => void }).focus === 'function') {
      ;(control as HTMLElement & { focus: () => void }).focus()
    }
  }

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)

    if (!this.id) {
      this.id = `n-field-label-${fieldLabelId++}`
    }

    this.addEventListener('click', this.handleClick)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('click', this.handleClick)
  }

  protected createRenderRoot() {
    return this
  }

  /** The control this label activates on click. */
  private controlForLabel(): Element | null {
    if (this.for) {
      const root = this.getRootNode() as Document | ShadowRoot
      return root.getElementById?.(this.for) ?? null
    }

    // No explicit `for`: activate the control in the enclosing Field, so
    // `<nord-field><nord-field-label>…</nord-field-label><nord-input></nord-input></nord-field>`
    // remains clickable.
    const field = this.closest('nord-field')
    return field ? this.controlInField(field) : null
  }

  /** The form control inside a Field — the first child that isn't a field part. */
  private controlInField(field: Element): Element | null {
    for (const el of Array.from(field.children)) {
      if (el === this || el.localName.startsWith('nord-field')) {
        continue
      }
      return el
    }
    return null
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-field-label': FieldLabel
  }
}
